/* Command palette (Ctrl+K; rule 48). Threads, screens, commands and settings in one list; type to
   filter, arrows to move, Enter to run, Esc to close. The shell fills it from the feature registry. */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';

export interface PaletteItem {
  id: string;
  title: string;
  group?: string;
  hint?: ReactNode;
  icon?: ReactNode;
  keywords?: string[];
  run: () => void;
}

export function filterPalette(items: PaletteItem[], q: string): PaletteItem[] {
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return items;
  const scored = items.map((it) => {
    const hay = [it.title, it.group ?? '', ...(it.keywords ?? [])].join(' ').toLowerCase();
    if (!terms.every((t) => hay.includes(t))) return null;
    const title = it.title.toLowerCase();
    const score = (title.startsWith(terms[0]!) ? 0 : title.includes(terms[0]!) ? 1 : 2);
    return { it, score };
  }).filter((x): x is { it: PaletteItem; score: number } => !!x);
  return scored.sort((a, b) => a.score - b.score).map((x) => x.it);
}

export function CommandPalette({ open, onClose, items, placeholder = 'Search chats, screens and commands…' }: { open: boolean; onClose: () => void; items: PaletteItem[]; placeholder?: string }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const results = useMemo(() => filterPalette(items, q), [items, q]);

  useEffect(() => { if (open) { setQ(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);
  useEffect(() => { setActive(0); }, [q]);

  if (!open) return null;
  const run = (it: PaletteItem | undefined) => { if (!it) return; onClose(); it.run(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastGroup: string | undefined;
  return createPortal(
    <div className="scrim top" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pal" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="row" style={{ gap: 0, paddingLeft: 14 }}>
          <Search size={18} aria-hidden="true" className="muted" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results[active] ? `${listId}-${results[active]!.id}` : undefined}
            aria-label="Search commands"
            placeholder={placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            style={{ flex: 1 }}
          />
        </div>
        <ul id={listId} role="listbox" aria-label="Results">
          {results.length === 0 && <li className="none" role="presentation">No matches. Try a screen name or a command.</li>}
          {results.map((it, i) => {
            const header = it.group && it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return [
              header && !q ? <li key={`g-${header}`} className="pal-grp" role="presentation">{header}</li> : null,
              <li
                key={it.id}
                id={`${listId}-${it.id}`}
                role="option"
                aria-selected={i === active}
                onPointerMove={() => setActive(i)}
                onClick={() => run(it)}
              >
                {it.icon}{it.title}{it.hint && <small>{it.hint}</small>}
              </li>,
            ];
          })}
        </ul>
        <div className="hint"><span><kbd>↑</kbd> <kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Esc</kbd> close</span></div>
      </div>
    </div>,
    document.body,
  );
}
